import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import './terms.css';

// Reusable animated component
function AnimatedSection({ children, className, id }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function TermsOfService() {
  const [activeSection, setActiveSection] = useState('introduction');
  const isClickScrolling = useRef(false);

  // Table of Contents
  const tableOfContents = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'account-registration', title: 'Account Registration' },
    { id: 'services', title: 'Services' },
    { id: 'intellectual-property', title: 'Intellectual Property' },
    { id: 'user-content', title: 'User Content' },
    { id: 'payments', title: 'Payments & Billing' },
    { id: 'confidentiality', title: 'Confidentiality' },
    { id: 'limitation-liability', title: 'Limitation of Liability' },
    { id: 'termination', title: 'Termination' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'changes-terms', title: 'Changes to Terms' },
    { id: 'contact', title: 'Contact Information' }
  ];

  // Last updated date
  const lastUpdated = new Date('2024-01-15').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Scroll to section
    const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    isClickScrolling.current = true; // pause the scroll-tracking while we scroll
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setTimeout(() => {
      isClickScrolling.current = false;
    }, 1200);
  };

  // Highlight the matching button while the user scrolls
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );

    tableOfContents.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="terms-container">
      {/* ===== HEADER SECTION ===== */}
      <section className="terms-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
        </div>
        <motion.div 
          className="terms-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-badge">
            <span>Legal Documentation</span>
          </div>
          <h1>Terms of <span className="gradient-text">Service</span></h1>
          <p className="hero-subtitle">
            Please read these terms carefully before using our services. By accessing or using 
            GLAXMORE IT services, you agree to be bound by these terms and our Privacy Policy.
          </p>
          <div className="last-updated">
            Last updated: {lastUpdated}
          </div>
        </motion.div>
      </section>

      {/* ===== QUICK SUMMARY ===== */}
      <AnimatedSection className="terms-summary">
        <div className="summary-cards">
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">📋</div>
            <h3>Agreement</h3>
            <p>Legal contract between you and GLAXMORE IT</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">⚖️</div>
            <h3>Compliance</h3>
            <p>Adheres to Sri Lankan and international laws</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">🔍</div>
            <h3>Transparency</h3>
            <p>Clear terms for service usage</p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* ===== MAIN CONTENT ===== */}
      <div className="terms-content-container">
        {/* ===== TABLE OF CONTENTS ===== */}
        <AnimatedSection className="table-of-contents">
          <div className="toc-header">
            <h2>Table of Contents</h2>
            <p>Navigate through our terms of service</p>
          </div>
          <nav className="toc-nav">
            <ul>
              {tableOfContents.map((item) => (
                <li key={item.id}>
                  <button
                    className={`toc-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </AnimatedSection>

        {/* ===== TERMS CONTENT ===== */}
        <div className="terms-sections">
          {/* INTRODUCTION */}
          <AnimatedSection id="introduction" className="terms-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to GLAXMORE IT ("Company", "we", "our", "us")! These Terms of Service ("Terms", 
              "Terms of Service") govern your use of our website located at glaxmore.lk (together or 
              individually "Service") operated by GLAXMORE IT.
            </p>
            <p>
              Our Privacy Policy also governs your use of our Service and explains how we collect, 
              safeguard and disclose information that results from your use of our web pages. 
              Your agreement with us includes these Terms and our Privacy Policy ("Agreements"). 
              You acknowledge that you have read and understood Agreements, and agree to be bound of them.
            </p>
          </AnimatedSection>

          {/* DEFINITIONS */}
          <AnimatedSection id="definitions" className="terms-section">
            <h2>2. Definitions</h2>
            <p>For the purposes of these Terms of Service:</p>
            <ul>
              <li><strong>"Company"</strong> refers to GLAXMORE IT, 33, Sri Dharmarama Road, Ratmalana, Sri Lanka.</li>
              <li><strong>"Country"</strong> refers to Sri Lanka.</li>
              <li><strong>"Service"</strong> refers to the website and any related services provided by GLAXMORE IT.</li>
              <li><strong>"You"</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
              <li><strong>"Content"</strong> refers to content such as text, images, or other information that can be posted, uploaded, linked to or otherwise made available by You.</li>
            </ul>
          </AnimatedSection>

          {/* ACCOUNT REGISTRATION */}
          <AnimatedSection id="account-registration" className="terms-section">
            <h2>3. Account Registration</h2>
            <p>
              When You create an account with Us, You must provide Us information that is accurate, 
              complete, and current at all times. Failure to do so constitutes a breach of the Terms, 
              which may result in immediate termination of Your account on Our Service.
            </p>
            <p>
              You are responsible for safeguarding the password that You use to access the Service and 
              for any activities or actions under Your password. You agree not to disclose Your password 
              to any third party. You must notify Us immediately upon becoming aware of any breach of 
              security or unauthorized use of Your account.
            </p>
          </AnimatedSection>

          {/* SERVICES */}
          <AnimatedSection id="services" className="terms-section">
            <h2>4. Services</h2>
            <p>
              GLAXMORE IT provides comprehensive IT solutions including but not limited to:
            </p>
            <ul>
              <li>Custom software development</li>
              <li>Cloud solutions and migration services</li>
              <li>AI and machine learning solutions</li>
              <li>Cybersecurity services</li>
              <li>IT consulting and digital transformation</li>
              <li>Mobile application development</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of the Service at any 
              time, including the availability of any feature, database, or content.
            </p>
          </AnimatedSection>

          {/* INTELLECTUAL PROPERTY */}
          <AnimatedSection id="intellectual-property" className="terms-section">
            <h2>5. Intellectual Property</h2>
            <p>
              The Service and its original content (excluding Content provided by users), features and 
              functionality are and will remain the exclusive property of GLAXMORE IT and its licensors. 
              The Service is protected by copyright, trademark, and other laws of both Sri Lanka and 
              foreign countries. Our trademarks and trade dress may not be used in connection with any 
              product or service without the prior written consent of GLAXMORE IT.
            </p>
            <p>
              For custom development projects, intellectual property rights will be assigned as specified 
              in individual project agreements. Typically, upon full payment, clients receive exclusive 
              rights to the custom-developed software.
            </p>
          </AnimatedSection>

          {/* USER CONTENT */}
          <AnimatedSection id="user-content" className="terms-section">
            <h2>6. User Content</h2>
            <p>
              You are responsible for the content that you post to the Service, including its legality, 
              reliability, and appropriateness. By posting Content to the Service, You grant Us the right 
              and license to use, modify, publicly perform, publicly display, reproduce, and distribute 
              such Content on and through the Service.
            </p>
            <p>
              You represent and warrant that: (i) the Content is Yours (You own it) or You have the right 
              to use it and grant Us the rights and license as provided in these Terms, and (ii) the posting 
              of Your Content on or through the Service does not violate the privacy rights, publicity rights, 
              copyrights, contract rights or any other rights of any person.
            </p>
          </AnimatedSection>

          {/* PAYMENTS & BILLING */}
          <AnimatedSection id="payments" className="terms-section">
            <h2>7. Payments & Billing</h2>
            <p>
              Certain aspects of the Service may be provided for a fee or other charge. If You elect to use 
              paid aspects of the Service, You agree to the pricing and payment terms as we may update them 
              from time to time.
            </p>
            <p>
              <strong>Payment Terms:</strong> Unless otherwise specified in a separate agreement, payments 
              are due within 30 days of invoice date. Late payments may be subject to interest charges of 
              1.5% per month.
            </p>
            <p>
              <strong>Taxes:</strong> All fees are exclusive of all taxes, levies, or duties imposed by 
              taxing authorities in Sri Lanka, and you shall be responsible for payment of all such taxes.
            </p>
          </AnimatedSection>

          {/* CONFIDENTIALITY */}
          <AnimatedSection id="confidentiality" className="terms-section">
            <h2>8. Confidentiality</h2>
            <p>
              Both parties agree to maintain the confidentiality of any proprietary information received 
              from the other party during the term of this agreement and for a period of three (3) years 
              after termination. "Proprietary Information" includes, but is not limited to, trade secrets, 
              know-how, inventions, techniques, processes, programs, ideas, algorithms, formulas, schematics, 
              testing procedures, software design and architecture, computer code, internal documentation, 
              design and function specifications, product requirements, problem reports, analysis and 
              performance information, and other technical, business, product, marketing and financial 
              information, plans and data.
            </p>
          </AnimatedSection>

          {/* LIMITATION OF LIABILITY */}
          <AnimatedSection id="limitation-liability" className="terms-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              Notwithstanding any damages that You might incur, the entire liability of GLAXMORE IT and any 
              of its suppliers under any provision of this Terms and Your exclusive remedy for all of the 
              foregoing shall be limited to the amount actually paid by You through the Service or 100 USD 
              if You haven't purchased anything through the Service.
            </p>
            <p>
              To the maximum extent permitted by applicable law, in no event shall GLAXMORE IT or its suppliers 
              be liable for any special, incidental, indirect, or consequential damages whatsoever (including, 
              but not limited to, damages for loss of profits, loss of data or other information, for business 
              interruption, for personal injury, loss of privacy arising out of or in any way related to the 
              use of or inability to use the Service, third-party software and/or third-party hardware used 
              with the Service, or otherwise in connection with any provision of this Terms), even if GLAXMORE IT 
              or any supplier has been advised of the possibility of such damages and even if the remedy fails 
              of its essential purpose.
            </p>
          </AnimatedSection>

          {/* TERMINATION */}
          <AnimatedSection id="termination" className="terms-section">
            <h2>10. Termination</h2>
            <p>
              We may terminate or suspend Your account immediately, without prior notice or liability, for 
              any reason whatsoever, including without limitation if You breach the Terms. Upon termination, 
              Your right to use the Service will immediately cease.
            </p>
            <p>
              If You wish to terminate Your account, You may simply discontinue using the Service. All 
              provisions of the Terms which by their nature should survive termination shall survive 
              termination, including, without limitation, ownership provisions, warranty disclaimers, 
              indemnity and limitations of liability.
            </p>
          </AnimatedSection>

          {/* GOVERNING LAW */}
          <AnimatedSection id="governing-law" className="terms-section">
            <h2>11. Governing Law</h2>
            <p>
              The laws of Sri Lanka shall govern these Terms and Your use of the Service. Your use of the 
              Service may also be subject to other local, state, national, or international laws.
            </p>
            <p>
              Any disputes arising under or in connection with these Terms shall be subject to the exclusive 
              jurisdiction of the courts located in Colombo, Sri Lanka.
            </p>
          </AnimatedSection>

          {/* CHANGES TO TERMS */}
          <AnimatedSection id="changes-terms" className="terms-section">
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. 
              If a revision is material We will make reasonable efforts to provide at least 30 days' notice 
              prior to any new terms taking effect. What constitutes a material change will be determined at 
              Our sole discretion.
            </p>
            <p>
              By continuing to access or use Our Service after those revisions become effective, You agree to 
              be bound by the revised terms. If You do not agree to the new terms, please stop using the Service.
            </p>
          </AnimatedSection>

          {/* CONTACT INFORMATION */}
          <AnimatedSection id="contact" className="terms-section">
            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="contact-details">
              <p><strong>GLAXMORE IT Technologies</strong></p>
              <p>33, Sri Dharmarama Road</p>
              <p>Ratmalana, Sri Lanka</p>
              <p>Email: <a href="mailto:support@glaxmore.com">support@glaxmore.com</a></p>
              <p>Phone: +94 70 1111 735</p>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* ===== ACCEPTANCE SECTION ===== */}
      <AnimatedSection className="acceptance-section">
        <div className="acceptance-content">
          <h2>Acceptance of Terms</h2>
          <p>
            By using our services, you acknowledge that you have read, understood, and agree to be bound 
            by these Terms of Service. If you do not agree with any part of these terms, you must not use 
            our services.
          </p>
          {/* <div className="acceptance-actions">
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download Terms (PDF)
            </motion.button>
            <motion.button 
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Legal Team
            </motion.button>
          </div> */}
        </div>
      </AnimatedSection>
    </div>
  );
}

export default TermsOfService;